import React from 'react';
import HeadData from 'components/head-data';
import Sitemap from 'components/sitemap';
import Layout from 'components/layout';
import { CountryCode, CountryCodeList } from 'types/countries';
import { useThemeContext, useUser } from 'contexts';
import { ThemeConfig } from 'themes';
import { createGetServerSideProps, HandlerType } from 'utils/next/shared-getServerSideProps';
import { createInsightsApi } from 'data/insights';
import { createSeoLinksApi } from 'data/seo-links';

import type { SitemapPlace } from 'components/sitemap';
import type { GetServerSidePropsContext } from 'next';
import { FeaturesType } from 'contexts';
import { isExpTenant, isTenantExpCommercial, isTenantExpCommercialCa, TenantTypes } from '@tenants';
import { Namespaces } from 'types/locales';
import { NOT_FOUND } from 'constants/not-found';

const countryToCountryCode = {
  ca: 'CA',
  us: 'US',
};

const handler: HandlerType<SitemapPageProps> = async (context: GetServerSidePropsContext, _features?: FeaturesType, tenant?: TenantTypes) => {
  if (isTenantExpCommercial(tenant.id) || isTenantExpCommercialCa(tenant.id)) {
    return NOT_FOUND;
  }
  const { country } = context.params || {}; // Extract country from URL params
  const seoLinksApi = createSeoLinksApi(createInsightsApi());
  const isCountrySpecificSitemap = isExpTenant(tenant.id);
  const siteCountryLocation = countryToCountryCode[country as keyof typeof countryToCountryCode] as CountryCode;

  const provincesRes = await seoLinksApi.getProvinceLinks({
    country: siteCountryLocation,
    addNameAffixes: false,
  });
  const provinces = provincesRes.links;

  const citiesRes = await seoLinksApi.getCityLinks({
    country: siteCountryLocation,
    limit: 9000,
    addNameAffixes: false,
  });
  const cities = citiesRes.links;

  return {
    props: {
      provinces,
      cities,
      country: country as string,
      isCountrySpecificSitemap,
    },
  };
};

export const getServerSideProps = createGetServerSideProps<SitemapPageProps>(handler, [
  Namespaces.COMMON,
]);

export type SitemapPageProps = {
  provinces: SitemapPlace[];
  cities: SitemapPlace[];
  country: string;
  isCountrySpecificSitemap: boolean;
};
  
export const headerData = (siteName: ThemeConfig['ogSiteName'], siteLocation?: CountryCode) => {
  return {
    title: `Real Estate Listings Canada — Find Homes for Sale on ${siteName}`, // TODO: Should we update the title here to reflect the locale shown?
    metaDescription: `List of all the cities and real estate listings in ${siteLocation === CountryCodeList.UNITED_STATES ? 'the United States' : 'Canada'} to search homes, condos and townhouses for sale.`,
  };
};

export default function SitemapPage({ provinces, cities, country, isCountrySpecificSitemap }: SitemapPageProps) {
  const { theme } = useThemeContext();
  const { siteLocation } = useUser();
  
  return (
    <Layout>
      <HeadData data={headerData(theme.ogSiteName, siteLocation)} />
      <Sitemap provinces={provinces} cities={cities} country={country} isCountrySpecificSitemap={isCountrySpecificSitemap} />
    </Layout>
  );
}